/**
 * API Error Utilities
 *
 * Helper functions for extracting and formatting API error messages.
 * Backend uses BusinessException pattern with structured error responses.
 *
 * Story 3.11: Added for visual assets and dictionary validation error handling
 */

import { AxiosError } from 'axios';

/**
 * Backend error response structure
 * Matches the format from common.exceptions.BusinessException
 */
interface ApiErrorResponse {
  error: {
    type: string;
    message: string;
    code: string;
    timestamp: string;
    request_id?: string;
    extra?: Record<string, unknown>;
  };
}

/**
 * Field validation error structure from Pydantic
 */
interface ValidationError {
  loc: (string | number)[];
  msg: string;
  type: string;
}

/**
 * Pydantic validation error response
 */
interface ValidationErrorResponse {
  detail: ValidationError[];
}

/**
 * Mapping of error codes to user-friendly messages
 * Story 3.11: Error codes for visual assets and dictionary
 */
const ERROR_CODE_MESSAGES: Record<string, string> = {
  // Visual assets errors
  INVALID_THUMBNAIL_URL: 'Invalid thumbnail URL format',
  INVALID_BACKGROUND_URL: 'Invalid background image URL format',
  IMAGE_URL_UNREACHABLE: 'Could not access the image URL',

  // Dictionary errors
  DICTIONARY_TOO_MANY_ENTRIES: 'Dictionary cannot have more than 10 entries',
  DICTIONARY_INVALID_WORD: 'Dictionary word is required',
  DICTIONARY_INVALID_EXPLANATION: 'Dictionary explanation is required',
  DICTIONARY_TOO_MANY_PHRASES: 'Dictionary entry cannot have more than 3 example phrases',
  DICTIONARY_TOO_FEW_PHRASES: 'Dictionary entry must have at least 1 example phrase',
  LOCALIZED_PHRASE_COUNT_MISMATCH: 'Localized phrases count must match original phrases count',

  // Episode errors
  EPISODE_NOT_FOUND: 'Episode not found',
  EPISODE_VALIDATION_ERROR: 'Episode data is invalid',
};

/**
 * Extract a user-friendly error message from an API error
 */
export function getApiErrorMessage(error: unknown): string {
  if (!error) {
    return 'An unexpected error occurred';
  }

  // Check if it's an Axios error with a response
  if (isAxiosError(error) && error.response?.data) {
    const data = error.response.data;

    // Check for structured error response (BusinessException format)
    if (isApiErrorResponse(data)) {
      const errorCode = data.error.code;
      // Return user-friendly message if we have one, otherwise use the API message
      return ERROR_CODE_MESSAGES[errorCode] || data.error.message;
    }

    // Check for Pydantic validation error response
    if (isValidationErrorResponse(data)) {
      return formatValidationErrors(data.detail);
    }

    // Check for simple detail message
    if (typeof data === 'object' && 'detail' in data) {
      const detail = (data as { detail: string | unknown }).detail;
      if (typeof detail === 'string') {
        return detail;
      }
    }
  }

  // Fallback to error message property
  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred';
}

/**
 * Extract error code from API error response
 */
export function getApiErrorCode(error: unknown): string | null {
  if (isAxiosError(error) && error.response?.data) {
    const data = error.response.data;
    if (isApiErrorResponse(data)) {
      return data.error.code;
    }
  }
  return null;
}

/**
 * Extract extra data from API error response
 */
export function getApiErrorExtra(error: unknown): Record<string, unknown> | null {
  if (isAxiosError(error) && error.response?.data) {
    const data = error.response.data;
    if (isApiErrorResponse(data)) {
      return data.error.extra || null;
    }
  }
  return null;
}

/**
 * Format Pydantic validation errors into a readable string
 */
function formatValidationErrors(errors: ValidationError[]): string {
  if (errors.length === 0) {
    return 'Validation error';
  }

  if (errors.length === 1) {
    const err = errors[0];
    const field = err.loc.slice(1).join('.');
    return field ? `${field}: ${err.msg}` : err.msg;
  }

  // Multiple errors - summarize
  return `${errors.length} validation errors. Please check the form fields.`;
}

/**
 * Type guard for AxiosError
 */
function isAxiosError(error: unknown): error is AxiosError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  );
}

/**
 * Type guard for structured API error response
 */
function isApiErrorResponse(data: unknown): data is ApiErrorResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'error' in data &&
    typeof (data as ApiErrorResponse).error === 'object' &&
    'code' in (data as ApiErrorResponse).error &&
    'message' in (data as ApiErrorResponse).error
  );
}

/**
 * Type guard for Pydantic validation error response
 */
function isValidationErrorResponse(data: unknown): data is ValidationErrorResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'detail' in data &&
    Array.isArray((data as ValidationErrorResponse).detail)
  );
}
